import { Check, ChevronDown } from "lucide-react";
import { Select as S } from "radix-ui";

import { cn } from "@/lib/cn";

export interface SelectOption {
  value: string;
  label: string;
}

/** A simple single-value select over Radix Select. */
export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  label,
  className,
}: {
  value: string | undefined;
  onValueChange: (v: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  /** Accessible name (shown to screen readers). */
  label: string;
  className?: string;
}) {
  return (
    <S.Root value={value} onValueChange={onValueChange}>
      <S.Trigger
        aria-label={label}
        className={cn(
          "inline-flex h-9 min-w-32 items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 text-sm",
          "text-fg data-[placeholder]:text-subtle",
          className,
        )}
      >
        <span className="min-w-0 truncate text-left">
          <S.Value placeholder={placeholder} />
        </span>
        <S.Icon>
          <ChevronDown className="size-4 text-subtle" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border bg-surface shadow-card"
        >
          <S.Viewport className="p-1">
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                className="relative flex h-8 cursor-pointer items-center rounded-md pr-2 pl-7 text-sm text-fg outline-none select-none data-[highlighted]:bg-surface-2"
              >
                <S.ItemIndicator className="absolute left-2">
                  <Check className="size-3.5" />
                </S.ItemIndicator>
                <S.ItemText>{o.label}</S.ItemText>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}
